import { CHAIN_IDS } from '@/constants/chain';

import {
  type CommonValidationInput,
  type CommonValidationResult,
  type QuoteErrorInput,
  appendHighPriceImpactResult,
  appendRequiredTokenResults,
  appendTokenRestrictionResults,
  isSameChainPair,
} from './common';

/** Code-discriminated validation outcomes only the market-swap validator emits. */
export type MarketValidationResult =
  | { readonly code: 'insufficient-fee-balance'; readonly severity: 'blocking' }
  | { readonly code: 'sell-amount-too-small'; readonly severity: 'blocking' }
  | {
      readonly code: 'quote-failed';
      readonly severity: 'blocking';
      readonly error: Error;
    }
  | {
      readonly code: 'high-fees';
      readonly severity: 'warning';
      readonly feeToSellRatio: number;
    };

/** Ordered results a market-swap validation can produce. */
export type MarketValidationResults = Array<CommonValidationResult | MarketValidationResult>;

/** Normalized facts needed to validate a same-chain market swap. */
export type MarketValidationInput = CommonValidationInput & {
  readonly quoteError: QuoteErrorInput | undefined;
  readonly quoteReportsInsufficientBalance: boolean;
  readonly requiresApproval: boolean;
  readonly hasInsufficientFeeBalance: boolean;
  readonly sellAmountUnits: bigint | undefined;
  readonly sellTokenBalanceUnits: bigint | undefined;
  readonly networkFeeUsd: number | undefined;
  readonly sellAmountUsd: number | undefined;
};

/** High fees warn inclusively at the legacy-primary 10% ratio for positive sell USD values. */
export const HIGH_FEE_WARNING_RATIO = 0.1;

function appendMarketBalanceResults(
  input: MarketValidationInput,
  hasInsufficientBalance: boolean,
  results: MarketValidationResults,
): void {
  if (hasInsufficientBalance) {
    results.push({ code: 'insufficient-balance', severity: 'blocking' });
  }
  if (input.hasInsufficientFeeBalance) {
    results.push({ code: 'insufficient-fee-balance', severity: 'blocking' });
  }
}

/** Validates a market swap and returns every applicable result in legacy-primary order. */
export function validateMarketSwap(input: MarketValidationInput): MarketValidationResults {
  const results: MarketValidationResults = [];
  const quoteErrorCode = input.quoteError?.code;
  // Classified unauthorized-token and insufficient-balance quote failures fold into
  // the restriction and balance results instead of surfacing as quote-failed.
  const hasInsufficientBalance =
    input.quoteReportsInsufficientBalance || quoteErrorCode === 'insufficient-balance';

  appendTokenRestrictionResults(
    {
      rwaBlockedTokenAddresses: input.rwaBlockedTokenAddresses,
      isBuyTokenBlocked: input.isBuyTokenBlocked || quoteErrorCode === 'buy-token-unauthorized',
      isSellTokenBlocked: input.isSellTokenBlocked || quoteErrorCode === 'sell-token-unauthorized',
    },
    results,
  );

  if (
    input.sellTokenChainId !== undefined &&
    input.buyTokenChainId !== undefined &&
    input.sellTokenChainId !== input.buyTokenChainId
  ) {
    results.push({
      code: 'cross-chain-unsupported',
      severity: 'blocking',
      sellTokenChainId: input.sellTokenChainId,
      buyTokenChainId: input.buyTokenChainId,
    });
  }

  if (quoteErrorCode === 'sell-amount-too-small') {
    results.push({ code: 'sell-amount-too-small', severity: 'blocking' });
  } else if (input.quoteError?.code === 'unknown') {
    results.push({
      code: 'quote-failed',
      severity: 'blocking',
      error: input.quoteError.error,
    });
  }

  if (input.checksApprovalBeforeBalance) {
    appendRequiredTokenResults(input, results);
    if (input.requiresApproval) {
      results.push({ code: 'token-needs-approval', severity: 'blocking' });
    }
    appendMarketBalanceResults(input, hasInsufficientBalance, results);
  } else {
    appendMarketBalanceResults(input, hasInsufficientBalance, results);
    appendRequiredTokenResults(input, results);
    if (input.requiresApproval) {
      results.push({ code: 'token-needs-approval', severity: 'blocking' });
    }
  }

  if (
    !hasInsufficientBalance &&
    input.sellTokenChainId === CHAIN_IDS.SOLANA &&
    input.sellAmountUnits !== undefined &&
    input.sellTokenBalanceUnits !== undefined &&
    input.sellAmountUnits > input.sellTokenBalanceUnits
  ) {
    results.push({ code: 'insufficient-balance', severity: 'blocking' });
  }

  if (
    isSameChainPair(input) &&
    input.networkFeeUsd !== undefined &&
    input.sellAmountUsd !== undefined &&
    input.sellAmountUsd > 0
  ) {
    const feeToSellRatio = input.networkFeeUsd / input.sellAmountUsd;
    if (feeToSellRatio >= HIGH_FEE_WARNING_RATIO) {
      results.push({ code: 'high-fees', severity: 'warning', feeToSellRatio });
    }
  }

  appendHighPriceImpactResult(input, results);

  return results;
}
